import type { SubscriptionDetails } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";
import type { CurrencyCode } from "schemas/project";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { Loader } from "@editor/components/common/Loader";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useOpenModal } from "@editor/hooks/useModal";
import { projectRouter } from "@editor/trpc/project";
import { subscriptionRouter } from "@editor/trpc/subscription";
import { getCurrencySymbol } from "@editor/utils/analytics";

import {
  LoadedSettings,
  SettingsContent,
  SettingsLayout,
} from "@/editor/components/shops-ui/settings/SettingsCard";
import { useUpdateWorkspace } from "@/features/shops/hooks/useUpdateWorkspace";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { skipToken } from "@tanstack/react-query";
import { Clock } from "lucide-react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatTimezoneDetailed } from "replo-utils/lib/timezone";
import { CURRENCIES } from "schemas/project";

export type WorkspaceSettings = Pick<
  Workspace,
  "name" | "timeZone" | "currency"
>;

// Helper hook to determine context based on URL
const useSettingsContext = () => {
  const location = useLocation();
  const isShopContext = location.pathname.includes("/shops/");
  return { isShopContext };
};

const WorkspaceSettingsForm: React.FC<{
  workspace: Workspace;
  isShopContext: boolean;
}> = ({ workspace, isShopContext }) => {
  const { updateWorkspace } = useUpdateWorkspace();
  const {
    handleSubmit,
    setValue,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<WorkspaceSettings>({
    defaultValues: {
      name: workspace.name,
      timeZone:
        workspace.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: workspace.currency ?? "USD",
    },
  });

  const handleSetValue = (
    key: keyof WorkspaceSettings,
    value: WorkspaceSettings[keyof WorkspaceSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateSettingsSections = [
    {
      title: "Workspace Name",
      component: (
        <NameInput
          initialName={watch("name")}
          onChange={(value) => handleSetValue("name", value)}
        />
      ),
    },
    // NOTE (Kurt, 2025-06-04): We only show timezone and currency if NOT in shop context.
    ...(!isShopContext
      ? [
          {
            title: "Timezone",
            component: (
              <TimeZoneSettings
                timeZone={watch("timeZone") ?? ""}
                onChange={(value) => handleSetValue("timeZone", value)}
              />
            ),
          },
          {
            title: "Currency",
            component: (
              <CurrencySettings
                currency={watch("currency") ?? "USD"}
                onChange={(value) => handleSetValue("currency", value)}
              />
            ),
          },
        ]
      : []),
  ];

  const handleWorkspaceUpdate = async (
    workspaceSettings: WorkspaceSettings,
  ) => {
    await updateWorkspace({
      id: workspace.id,
      data: workspaceSettings,
    });
    reset({}, { keepValues: true });
  };

  return (
    <div className="flex flex-col gap-4">
      {updateSettingsSections.map((section) => {
        return (
          <SettingsContent key={section.title} title={section.title}>
            {section.component}
          </SettingsContent>
        );
      })}
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleWorkspaceUpdate)()}
          disabled={!isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const TimeZoneSettings: React.FC<{
  timeZone: string;
  onChange: (value: string) => void;
}> = ({ timeZone, onChange }) => {
  const timezoneOptions = Intl.supportedValuesOf("timeZone").map(
    (timezone) => ({
      label: formatTimezoneDetailed(timezone),
      value: timezone,
    }),
  );

  timezoneOptions.unshift({
    label: formatTimezoneDetailed("UTC"),
    value: "UTC",
  });

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={timezoneOptions}
        value={timeZone ?? undefined}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={formatTimezoneDetailed(timeZone ?? "")}
            titleAlignment="start"
            isPlaceholder={!Boolean(timeZone)}
            startEnhancer={<Clock size={14} />}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported using this timezone.
      </div>
    </div>
  );
};

const CurrencySettings: React.FC<{
  currency: CurrencyCode;
  onChange: (value: string) => void;
}> = ({ currency, onChange }) => {
  const formatCurrencyLabel = (code: CurrencyCode, name: string) => {
    const symbol = getCurrencySymbol(code);
    // NOTE (Kurt, 2025-04-02): Some currency symbols aren't rendered consistently in the browser, so the
    // Intl API returns the currency code instead, if we get a code back we don't want to show it since we
    // already show the currency code in the label.
    return `${code} - ${name}${symbol !== code ? ` (${symbol})` : ""}`;
  };

  const currencyOptions = Object.entries(CURRENCIES).map(
    ([code, { name }]) => ({
      label: formatCurrencyLabel(code as CurrencyCode, name),
      value: code,
    }),
  );

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={currencyOptions}
        value={currency}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={formatCurrencyLabel(currency, CURRENCIES[currency].name)}
            titleAlignment="start"
            isPlaceholder={!Boolean(currency)}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported in this currency.
      </div>
    </div>
  );
};

const NameInput = ({
  initialName,
  onChange,
}: {
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your workspace name"
        size="base"
      />
    </div>
  );
};

type WorkspaceDeleteSectionProps = {
  numberOfProjects: number;
  numberOfReferralCodes?: number;
  subscription?: SubscriptionDetails | null;
  isOwner: boolean;
  workspaceId: string;
};

const WorkspaceDeleteSection: React.FC<WorkspaceDeleteSectionProps> = ({
  numberOfProjects,
  numberOfReferralCodes = 0,
  subscription,
  isOwner,
  workspaceId,
}) => {
  const openModal = useOpenModal();

  const canDelete =
    numberOfProjects === 0 &&
    numberOfReferralCodes === 0 &&
    (!subscription || Boolean(subscription?.endedAt)) &&
    isOwner;

  const deleteRequirements = [
    {
      text: "Delete all shops",
      subtitle: `${numberOfProjects} ${numberOfProjects === 1 ? "shop" : "shops"}`,
    },
    ...(subscription && !subscription.endedAt
      ? [
          {
            text: "Cancel your subscription",
            subtitle: `${subscription.v1BillingTier} plan`,
          },
        ]
      : []),
    {
      text: "Delete all referral codes",
      subtitle: `${numberOfReferralCodes} ${numberOfReferralCodes === 1 ? "code" : "codes"}`,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {!canDelete && (
        <div className="flex flex-col gap-2">
          <span className="text-muted text-sm">
            To delete this workspace you must:
          </span>
          <ul className="text-muted list-inside list-disc text-sm">
            {deleteRequirements.map((requirement) => (
              <li key={requirement.text}>
                {requirement.text}{" "}
                <span className="text-sm font-medium">
                  [{requirement.subtitle}]
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="text-muted text-sm">
        Deleting this workspace is a permanent action. Deleted workspaces cannot
        be recovered without the help of Replo support.
      </div>
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="danger"
          size="base"
          disabled={!canDelete}
          onClick={() => {
            openModal({
              type: "deleteWorkspaceModal",
              props: {
                workspaceId,
              },
            });
          }}
        >
          Delete Workspace
        </Button>
      </div>
    </div>
  );
};

const WorkspaceSettingsWrapper = () => {
  const { isShopContext } = useSettingsContext();

  const { workspace, isLoading: isLoadingWorkspace } =
    useCurrentWorkspaceContext();

  const workspaceId = workspace?.id;
  const projectId = useCurrentProjectId();
  const { data: projectList, isLoading: isLoadingWorkspaceProjectData } =
    projectRouter.listWithStats.useQuery({ workspaceId });

  const { data: workspaceSubscription, isLoading: isLoadingSubscription } =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspaceId ? { workspaceId, projectId } : skipToken,
    );

  const userIsOwner = useIsWorkspaceOwner(workspaceId);
  const currentProjectContext = useCurrentProjectContext();
  const { userRole } = currentProjectContext ?? {};

  // In workspace-only mode, use workspace owner status; otherwise use project context
  const canDeleteWorkspace = !currentProjectContext?.project
    ? userIsOwner
    : exhaustiveSwitch({ type: userRole ?? "none" })({
        superuser: () => true,
        workspaceOwner: () => true,
        workspaceMember: () => false,
        projectMember: () => false,
        none: () => false,
      });

  const isLoading =
    isLoadingWorkspace ||
    isLoadingWorkspaceProjectData ||
    isLoadingSubscription;

  if (!workspace || !projectList) {
    return null;
  }

  const sections = [
    {
      title: "Workspace Settings",
      component: (
        <WorkspaceSettingsForm
          workspace={workspace}
          isShopContext={isShopContext}
        />
      ),
    },
  ];

  if ((isShopContext && canDeleteWorkspace) || !isShopContext) {
    sections.push({
      title: "Delete Workspace",
      component: (
        <WorkspaceDeleteSection
          numberOfProjects={projectList.projects.length}
          numberOfReferralCodes={workspace.referralCodes?.length ?? 0}
          subscription={workspaceSubscription ?? null}
          isOwner={userIsOwner}
          workspaceId={workspace.id}
        />
      ),
    });
  }

  return (
    <div className="flex flex-col flex-1 gap-4 px-6">
      <SettingsLayout>
        {isLoading ? <Loader /> : <LoadedSettings sections={sections} />}
      </SettingsLayout>
    </div>
  );
};

export default WorkspaceSettingsWrapper;
